import { UNCATEGORIZED_ACCOUNT_ROLES } from "@common/helpers/accounting/uncategorized-account";
import { injectReviewColumns } from "@common/helpers/inject-review-columns";
import { CloseTemplateBasis } from "@common/types/closeTemplateBasis";
import { CloseTemplateReviewField } from "@common/types/closeTemplateReviewField";
import { TransactionFilters } from "@common/schemas/transaction.schema";
import {
    TransactionColumn,
    TransactionView,
} from "@common/types/views/transactionViews";
import { ViewType } from "@common/types/views/views";
import {
    transactionReviewCloseTemplateViewConfigSchema,
    transactionsCloseTemplateViewConfigSchema,
} from "@common/schemas/close-template-task-view-config.schema";

const DEFAULT_TRANSACTION_OPTIONS = {
    bulkActions: false,
    selection: false,
} as const;

function defaultColumns(
    basis: CloseTemplateBasis | null,
): TransactionView["columns"] {
    return [
        { id: TransactionColumn.DATE },
        { id: TransactionColumn.DESCRIPTION },
        { id: TransactionColumn.CATEGORY },
        {
            id:
                basis === CloseTemplateBasis.ACCRUAL
                    ? TransactionColumn.ACCRUAL_LEDGER
                    : TransactionColumn.LEDGER,
        },
        { id: TransactionColumn.AMOUNT },
    ];
}

export interface CreateTransactionsViewConfigParams {
    filters?: TransactionFilters;
    basis?: CloseTemplateBasis | null;
    columns?: TransactionView["columns"];
    stickyViewType?: "sticky" | "dynamic";
}

export function createTransactionsViewConfig({
    filters = {},
    basis = null,
    columns,
    stickyViewType,
}: CreateTransactionsViewConfigParams = {}) {
    return transactionsCloseTemplateViewConfigSchema.parse({
        type: ViewType.TRANSACTION,
        columns: columns ?? defaultColumns(basis),
        options: DEFAULT_TRANSACTION_OPTIONS,
        filters,
        ...(stickyViewType != null ? { stickyViewType } : {}),
    });
}

export interface CreateTransactionReviewViewConfigParams {
    reviewFields: [CloseTemplateReviewField, ...CloseTemplateReviewField[]];
    basis?: CloseTemplateBasis | null;
    filters?: TransactionFilters;
}

export function createTransactionReviewViewConfig({
    reviewFields,
    basis = null,
    filters = {},
}: CreateTransactionReviewViewConfigParams) {
    const base: TransactionView = {
        type: ViewType.TRANSACTION,
        columns: defaultColumns(basis),
        options: DEFAULT_TRANSACTION_OPTIONS,
        filters,
    };
    return transactionReviewCloseTemplateViewConfigSchema.parse({
        ...injectReviewColumns(base, reviewFields),
        reviewFields,
    });
}

export function createUncategorizedReviewViewConfig() {
    return transactionReviewCloseTemplateViewConfigSchema.parse({
        type: ViewType.TRANSACTION,
        columns: [
            { id: TransactionColumn.DATE },
            { id: TransactionColumn.BANK_DESCRIPTION },
            { id: TransactionColumn.COUNTERPARTY },
            { id: TransactionColumn.ACCOUNT },
            { id: TransactionColumn.AMOUNT },
            { id: TransactionColumn.ENTITY },
            {
                id: TransactionColumn.LEDGER,
                options: {
                    pin: "right",
                    customVariants: { variant: "review" },
                },
            },
            { id: TransactionColumn.REVIEW, options: { pin: "right" } },
        ],
        options: DEFAULT_TRANSACTION_OPTIONS,
        filters: {
            cashGeneralLedgerRoles: [...UNCATEGORIZED_ACCOUNT_ROLES],
        },
        reviewFields: ["ledger"] satisfies CloseTemplateReviewField[],
    });
}
